import {
  createContext,
  useEffect,
  useState,
  ReactNode,
} from "react";

import { GoogleAuthRequest, LoginRequest, RegisterWorkerRequest, User } from "../types/auth.types";
import { storage } from "../utils/storage";
import { authService } from "@/api/auth/auth.service";
import { workerService } from "@/api/workers/worker.service";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (data: LoginRequest) => Promise<void>;
  register: (data: RegisterWorkerRequest) => Promise<void>;
  googleAuth: (data: GoogleAuthRequest) => Promise<void>;
  acceptPrivacyPolicy: (userId: string) => Promise<User>;
  logout: () => void;
  isAuthenticated: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface Props {
  children: ReactNode;
}

export const AuthProvider = ({ children }: Props) => {
  const [user, setUser] = useState<User | null>(storage.getUser());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = !!user;

  const login = async (payload: LoginRequest) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authService.loginWorker(payload);
        console.log(response);
      if (!response.success) {
        throw new Error("Login failed");
      }

      storage.setAuth(response.data);
      setUser(response.data.user);

    } catch (err: any) {
      setError(err?.response?.data?.message || "Error inesperado");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const register = async (payload: RegisterWorkerRequest) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authService.registerWorker(payload);
      if (!response.success) {
        throw new Error((response.data as any)?.message);
      }

      storage.setAuth(response.data);
      setUser(response.data.user);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Error inesperado');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const googleAuth = async (payload: GoogleAuthRequest) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authService.googleAuthWorker(payload);
      if (!response.success) {
        throw new Error('Google auth failed');
      }

      storage.setAuth(response.data);
      setUser(response.data.user);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error inesperado');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const acceptPrivacyPolicy = async (userId: string) => {
    try {
      setLoading(true);
      setError(null);

      const updatedUser = await workerService.acceptPrivacyPolicy(userId);
      const accessToken = localStorage.getItem('accessToken') || '';
      const refreshToken = localStorage.getItem('refreshToken') || '';

      storage.setAuth({ accessToken, refreshToken, user: updatedUser });
      setUser(updatedUser);
      return updatedUser;
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error inesperado');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    storage.clearAuth();
    setUser(null);
  };

  // Mantener sesión al recargar
  useEffect(() => {
    const storedUser = storage.getUser();
    if (storedUser) {
      setUser(storedUser);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login,
        register,
        googleAuth,
        acceptPrivacyPolicy,
        logout,
        isAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
